import { Reveal, SectionLabel, CountUp } from "../Reveal";
import { MEDIA_KIT } from "../../data/content";
import { TrendingUp, MapPin, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

export const MediaKit = () => (
  <section id="mediakit" className="relative py-24 sm:py-32 md:py-40 bg-noise border-t border-white/10" data-testid="mediakit-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
      <SectionLabel color="#22AED4">{MEDIA_KIT.label}</SectionLabel>
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[0.95] mb-14">
          {MEDIA_KIT.heading}
        </h2>
      </Reveal>

      {/* socials */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {MEDIA_KIT.socials.map((s, i) => (
          <Reveal key={s.handle} delay={i * 0.08}>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-link-${s.platform.toLowerCase()}-${i}`}
              className="group block border border-white/10 bg-[#0A0A0A] p-7 h-full hover:border-[#22AED4]/40 hover:bg-[#0E0E0E] transition-colors"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-display text-sm uppercase tracking-widest text-white/50">{s.platform}</span>
                <ExternalLink size={16} className="text-white/30 group-hover:text-[#22AED4] transition-colors" />
              </div>
              <p className="font-display text-lg text-white mb-4 group-hover:text-[#22AED4] transition-colors">{s.handle}</p>
              <div className="flex items-end gap-6">
                <div>
                  <p className="font-display text-4xl font-bold text-[#BAF173] leading-none"><CountUp value={s.followers} /></p>
                  <p className="text-xs text-white/40 mt-1 uppercase tracking-widest">Followers</p>
                </div>
                {s.views && (
                  <div>
                    <p className="font-display text-4xl font-bold text-white leading-none"><CountUp value={s.views} /></p>
                    <p className="text-xs text-white/40 mt-1 uppercase tracking-widest">Views / mo</p>
                  </div>
                )}
              </div>
              <p className="mt-5 text-xs text-[#11E5B4] uppercase tracking-wide">{s.note}</p>
            </a>
          </Reveal>
        ))}
      </div>

      {/* audience + market bento */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="border border-white/10 bg-[#0A0A0A] p-7 h-full">
            <div className="flex items-center gap-3 mb-5">
              <MapPin size={18} className="text-[#6735FA]" />
              <h3 className="font-display text-lg uppercase tracking-wide text-white">Audience</h3>
            </div>
            <p className="font-display text-6xl font-bold text-white leading-none">{MEDIA_KIT.audience.age}</p>
            <p className="text-xs text-white/40 uppercase tracking-widest mt-2 mb-5">Age range</p>
            <p className="text-sm text-white/60 leading-relaxed mb-3">{MEDIA_KIT.audience.profile}</p>
            <p className="text-sm text-white/45">{MEDIA_KIT.audience.location}</p>

            <div className="mt-6 space-y-3">
              {MEDIA_KIT.audience.split.map((sp, i) => (
                <div key={sp.label}>
                  <div className="flex justify-between text-xs uppercase tracking-widest text-white/50 mb-1.5">
                    <span>{sp.label}</span><span><CountUp value={`${sp.value}%`} /></span>
                  </div>
                  <div className="h-1.5 bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-[#11E5B4]"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${sp.value}%` }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="border border-white/10 bg-[#0A0A0A] p-7 h-full">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp size={18} className="text-[#BAF173]" />
              <h3 className="font-display text-lg uppercase tracking-wide text-white">Market Opportunity</h3>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {MEDIA_KIT.market.map((m) => (
                <div key={m.stat} className="bg-[#0A0A0A] p-6 transition-transform duration-300 hover:-translate-y-1" data-testid="market-stat">
                  <p className="font-display text-4xl md:text-5xl font-bold text-[#BAF173] leading-none"><CountUp value={m.stat} /></p>
                  <p className="text-sm text-white/55 mt-3 leading-snug">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
